import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { usePrivyWrapper } from '../hooks/usePrivyWrapper'
import { redeemWithEmail } from '../api'
import { RedeemSchema } from '../lib/schemas'
import { SubmitButton } from '../components/SubmitButton'
import { useToasts } from 'react-toast-notifications'
import { ValidationError } from 'yup'

interface RedeemFormProps {
  email: string
  uid: string
  onAddressSuccess: (address: string) => void
  onTransactionRequestChange: (transactionRequestId?: number) => void
}

interface RedeemFormValues {
  address: string
}

const initialFormValues: RedeemFormValues = { address: '' }

const RedeemForm: React.FC<RedeemFormProps> = ({ email, uid, onAddressSuccess, onTransactionRequestChange }) => {
  const [formValues, setFormValues] = useState(initialFormValues)
  const [touched, setTouched] = useState<{ [key in keyof RedeemFormValues]?: boolean }>({})
  const [errors, setErrors] = useState<{ [key in keyof RedeemFormValues]?: string }>({})
  const [isValid, setIsValid] = useState(false)
  const [isRedeemLoading, setIsRedeemLoading] = useState(false)
  const [transactionRequestId, setTransactionRequestId] = useState<number | undefined>(undefined)
  const { connectWallet: connectWalletWithPrivy, lastConnectedWallet } = usePrivyWrapper()

  const { addToast } = useToasts()

  useEffect(() => {
    if (lastConnectedWallet) {
      setFormValues({ address: lastConnectedWallet.address })
      setTouched({ address: true })
    }
  }, [lastConnectedWallet])

  useEffect(() => {
    onTransactionRequestChange(transactionRequestId)
  }, [transactionRequestId, onTransactionRequestChange])

  const validateForm = useCallback(() => {
    RedeemSchema.validate(formValues, { abortEarly: false })
      .then((validatedForm) => {
        setErrors({})
        setIsValid(true)
        onAddressSuccess(validatedForm.address)
      })
      .catch((err) => {
        if (err instanceof ValidationError) {
          const newErrors = err.inner.reduce(
            (acc: { [key: string]: string }, curr: any) => ({ ...acc, [curr.path]: curr.message }),
            {},
          )
          setErrors(newErrors)
          setIsValid(false)
        } else {
          console.error(err)
        }
      })
  }, [formValues, onAddressSuccess])

  useEffect(() => {
    if (Object.values(touched).some((touch) => touch)) {
      validateForm()
    }
  }, [formValues, touched, validateForm])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFormValues({ ...formValues, [name]: value })
    setTouched({ ...touched, [name]: true })
  }

  const handleForm = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!email || !isValid) return

    setIsRedeemLoading(true)
    redeemWithEmail(formValues.address, uid, email)
      .then((response) => {
        setTransactionRequestId(response.transactionRequestId)
      })
      .catch((error) => {
        addToast(error.message, {
          appearance: 'error',
          autoDismiss: false,
        })
      })
      .finally(() => setIsRedeemLoading(false))
  }

  return (
    <form onSubmit={handleForm} className="claim-form">
      <div className="redeem-text">
        These POAPs were claimed with email <span>{email}</span>, please enter your Ethereum address or ENS to claim
        them
      </div>
      <input
        type="text"
        autoComplete="off"
        className={classNames({ error: !!errors.address, 'redeem-reservation-input': true })}
        placeholder="Input your Ethereum address or ENS"
        name="address"
        onChange={handleChange}
        value={formValues.address}
        disabled={isRedeemLoading || !!transactionRequestId}
      />
      <p className="web3-input" onClick={connectWalletWithPrivy}>
        Get my address
      </p>
      {!transactionRequestId && (
        <SubmitButton className="mb-24" text="Continue" isSubmitting={isRedeemLoading} canSubmit={isValid} />
      )}
    </form>
  )
}

export default RedeemForm
